import React, { useCallback, useRef, useState } from 'react';
import {
  Box, Stack, Typography, IconButton, FormControl, FormLabel, FormHelperText, alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { formFieldStyles } from './FormField.styles';
import { getDocumentFileTypeMeta } from '../../constants/tableConfigurations';

// Bytes -> "1.2 MB" formati. Ust seviyede tekrar kullanmiyoruz cunku
// FileField'in kendi kontrolu icin yeterli.
const formatFileSize = (bytes) => {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// DynamicForm FormField pattern'ine uyumlu dosya alani. Drag&drop bolgesi
// + secilen dosya icin ikon/isim/boyut preview. Value = File | null. onChange
// (name, File, event) sinyaliyle DynamicForm'a bilgi verir.
const FileField = ({ field, value, onChange, onBlur, error, disabled = false }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const emit = useCallback((file, event) => {
    if (onChange) onChange(field.name, file || null, event);
  }, [field.name, onChange]);

  const handleClick = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const handleInputChange = (e) => {
    const f = e.target.files?.[0] || null;
    emit(f, e);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    const f = e.dataTransfer?.files?.[0];
    if (f) emit(f, e);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    if (inputRef.current) inputRef.current.value = '';
    emit(null, e);
  };

  const file = value instanceof File ? value : null;
  const meta = file ? getDocumentFileTypeMeta(file.type || file.name) : null;

  return (
    <FormControl fullWidth error={!!error} disabled={disabled} sx={formFieldStyles.field}>
      {field.label && (
        <FormLabel component="legend" sx={{ mb: 1, fontWeight: 500 }}>
          {t(field.label)}
          {field.required ? ' *' : ''}
        </FormLabel>
      )}

      <Box
        onClick={handleClick}
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onBlur={onBlur ? (e) => onBlur(field.name, value, e) : undefined}
        sx={{
          p: 2.5,
          borderRadius: 2,
          textAlign: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          border: '2px dashed',
          borderColor: error
            ? 'error.main'
            : dragOver
              ? 'primary.main'
              : 'divider',
          bgcolor: dragOver
            ? alpha(theme.palette.primary.main, 0.06)
            : 'action.hover',
          transition: 'all 0.15s ease-in-out',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <input
          ref={inputRef}
          type="file"
          hidden
          accept={field.accept}
          onChange={handleInputChange}
          disabled={disabled}
        />
        {file ? (
          <Stack direction="row" alignItems="center" spacing={1.5} justifyContent="center">
            {meta && <meta.Icon sx={{ fontSize: 32, color: meta.color }} />}
            <Box sx={{ textAlign: 'left', flex: 1 }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {file.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatFileSize(file.size)}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={handleClear}
              disabled={disabled}
              aria-label={t('common.clear', 'Temizle')}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        ) : (
          <>
            <CloudUploadIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 0.5 }} />
            <Typography variant="body2" color="text.secondary">
              {t(field.placeholder || 'documentBank.dropzone.title', 'Dosyayı sürükle veya seç')}
            </Typography>
            {field.hint && (
              <Typography variant="caption" color="text.disabled" display="block">
                {t(field.hint)}
              </Typography>
            )}
          </>
        )}
      </Box>
      {error && <FormHelperText error>{error}</FormHelperText>}
    </FormControl>
  );
};

export default FileField;
